import QRCode from 'qrcode';
import { luminance } from './theme';
import { BRAND } from '../config/brand';

/**
 * Print helpers for the plastic NFC cards (black PVC NTAG215 blanks).
 *
 * Size: CR80 / ISO/IEC 7810 ID-1, the standard size of these pre-made blanks.
 * The blanks come finished, so there is no bleed. Everything important stays
 * inside the safe zone (SAFE mm from every edge). The NFC antenna is inside
 * the card, so it can be printed over.
 */
export const CARD_MM = { width: 85.6, height: 54, radius: 3.18, thickness: 0.84, safe: 3 };

export const DPI_OPTIONS = [600, 300];
export const DEFAULT_DPI = 600;

/** Printed cards always point at production, whatever site generated them. */
export const PRINT_BASE_URL = `${BRAND.appUrl}/c/`;
export const printCardUrl = (slug) => `${PRINT_BASE_URL}${slug}`;

export const mmToPx = (mm, dpi) => Math.round((mm / 25.4) * dpi);
export const cardPixels = (dpi) => ({ width: mmToPx(CARD_MM.width, dpi), height: mmToPx(CARD_MM.height, dpi) });

/** carduzo-<slug>-<side>[-<suffix>].<ext>, e.g. carduzo-ana-front-white.png */
export const printFileName = (slug, side, ext, suffix = '') => `carduzo-${slug}-${side}${suffix ? `-${suffix}` : ''}.${ext}`;

/* ------------------------------------------------------------------ colors */

/** WCAG contrast ratio of a color against the black card. */
export const contrastOnBlack = (hex) => (luminance(hex) + 0.05) / 0.05;

/**
 * A brand color that reads on a black card (contrast >= 4.5 against black),
 * trying the accent first, then the primary. White when neither is light enough.
 */
export function printAccent(theme) {
  for (const c of [theme?.accent_color, theme?.primary_color]) {
    if (/^#[0-9A-Fa-f]{6}$/.test(c || '') && contrastOnBlack(c) >= 4.5) return { color: c.toUpperCase(), fromBrand: true };
  }
  return { color: '#FFFFFF', fromBrand: false };
}

/* ------------------------------------------------------------------- fonts */

// OFL fonts shipped in public/fonts/print so the art can embed them (an SVG
// drawn as an image cannot reach Google Fonts).
const FONT_FILES = [
  { family: 'Carduzo Print Display', weight: 700, file: 'outfit-latin-700-normal.woff2' },
  { family: 'Carduzo Print Display', weight: 800, file: 'outfit-latin-800-normal.woff2' },
  { family: 'Carduzo Print Sans', weight: 400, file: 'instrument-sans-latin-400-normal.woff2' },
  { family: 'Carduzo Print Sans', weight: 500, file: 'instrument-sans-latin-500-normal.woff2' },
  { family: 'Carduzo Print Sans', weight: 600, file: 'instrument-sans-latin-600-normal.woff2' },
];
export const DISPLAY_FONT = "'Carduzo Print Display', 'Outfit', sans-serif";
export const SANS_FONT = "'Carduzo Print Sans', 'Instrument Sans', sans-serif";

const blobToDataUrl = (blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(String(reader.result));
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });

let fontsPromise = null;
/**
 * Loads the print fonts once: registers them with the page (for measuring
 * text) and returns an @font-face block with the files inlined (for the SVG).
 */
export function loadPrintFonts() {
  if (!fontsPromise) {
    fontsPromise = Promise.all(
      FONT_FILES.map(async (f) => {
        const res = await fetch(`/fonts/print/${f.file}`);
        if (!res.ok) throw new Error(`Font ${f.file} missing`);
        const blob = await res.blob();
        const dataUrl = (await blobToDataUrl(blob)).replace(/^data:[^;,]*/, 'data:font/woff2');
        try {
          const face = new FontFace(f.family, await blob.arrayBuffer(), { weight: String(f.weight) });
          await face.load();
          document.fonts.add(face);
        } catch (err) {
          console.warn('Print font could not be registered:', err);
        }
        return `@font-face{font-family:'${f.family}';font-weight:${f.weight};font-style:normal;src:url(${dataUrl}) format('woff2');}`;
      })
    )
      .then((rules) => rules.join(''))
      .catch((err) => {
        console.error('Print fonts failed to load:', err);
        fontsPromise = null;
        return '';
      });
  }
  return fontsPromise;
}

let measureCtx = null;
/** Width in mm of text set at sizeMm (letterSpacingMm added per character). */
export function textWidthMm(text, { family, weight, sizeMm, letterSpacingMm = 0 }) {
  if (!measureCtx) measureCtx = document.createElement('canvas').getContext('2d');
  const scale = 20; // measure at 20 px per mm for precision
  measureCtx.font = `${weight} ${sizeMm * scale}px ${family}`;
  const chars = [...(text || '')].length;
  return measureCtx.measureText(text || '').width / scale + Math.max(0, chars - 1) * letterSpacingMm;
}

/** The largest font size (<= sizeMm, >= minMm) that fits maxWidthMm. */
export function fitSize(text, opts, maxWidthMm, minMm = opts.sizeMm * 0.55) {
  const w = textWidthMm(text, opts);
  if (w <= maxWidthMm) return opts.sizeMm;
  return Math.max(minMm, Math.floor(((opts.sizeMm * maxWidthMm) / w) * 100) / 100);
}

/* -------------------------------------------------------------------- logo */

const loadImage = (src) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Image could not be drawn'));
    img.src = src;
  });

/** Give an SVG explicit pixel width/height (from its viewBox) so every browser can draw it. */
function sizedSvg(text, target = 1600) {
  const doc = new DOMParser().parseFromString(text, 'image/svg+xml');
  const svg = doc.documentElement;
  if (!svg || svg.nodeName.toLowerCase() !== 'svg') throw new Error('Not an SVG');
  const vb = (svg.getAttribute('viewBox') || '').trim().split(/[\s,]+/).map(Number);
  let w = parseFloat(svg.getAttribute('width'));
  let h = parseFloat(svg.getAttribute('height'));
  if (vb.length === 4 && vb[2] > 0 && vb[3] > 0) { w = vb[2]; h = vb[3]; }
  if (!(w > 0 && h > 0)) { w = target; h = target; }
  if (vb.length !== 4) svg.setAttribute('viewBox', `0 0 ${w} ${h}`);
  const s = target / Math.max(w, h);
  svg.setAttribute('width', String(Math.round(w * s)));
  svg.setAttribute('height', String(Math.round(h * s)));
  svg.setAttribute('preserveAspectRatio', 'xMidYMid meet');
  return { svg: new XMLSerializer().serializeToString(svg), aspect: w / h };
}

/**
 * Fetch the company logo (from Storage) and prepare it for a black card.
 * Returns { original, white, aspect, mostlyDark, opaqueBackground, isSvg }:
 *  - original: data URL (SVG logos stay vector)
 *  - white: PNG data URL of the logo recolored to white ink
 *  - color: the logo in its own colors for a black card (a solid background removed)
 *  - colorContrast: how readable `color` is on black (below ~2 it nearly disappears)
 *  - mostlyDark: the logo is dark or has its own background
 */
export async function prepareLogoArt(url, maxPx = 2000) {
  const res = await fetch(url, { mode: 'cors' });
  if (!res.ok) throw new Error('Logo could not be loaded');
  let blob = await res.blob();
  const isSvg = blob.type.includes('svg') || /\.svg(\?|$)/i.test(url);
  let original;
  let drawSrc;
  let aspect;
  if (isSvg) {
    const sized = sizedSvg(await blob.text(), maxPx);
    blob = new Blob([sized.svg], { type: 'image/svg+xml' });
    original = await blobToDataUrl(blob);
    drawSrc = original;
    aspect = sized.aspect;
  } else {
    original = await blobToDataUrl(blob);
    drawSrc = original;
  }
  const img = await loadImage(drawSrc);
  const nw = img.naturalWidth || maxPx;
  const nh = img.naturalHeight || Math.round(maxPx / (aspect || 1));
  aspect = aspect || nw / nh;
  const scale = Math.min(maxPx / Math.max(nw, nh), isSvg ? 10 : 4);
  const w = Math.max(1, Math.round(nw * scale));
  const h = Math.max(1, Math.round(nh * scale));

  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  ctx.drawImage(img, 0, 0, w, h);
  const data = ctx.getImageData(0, 0, w, h);
  const px = data.data;

  const { opaqueBackground, bgLight, meanLum } = analyzePixels(px, w, h);
  const mostlyDark = opaqueBackground || meanLum < 0.35;

  // Own colors on black: a raster logo's solid background is keyed out (pixels close
  // to the edge color become transparent, edges are un-blended from it), so the
  // logo keeps its colors without printing a box.
  let color = original;
  let colorLum = meanLum * meanLum; // analyzePixels returns sqrt(luminance)
  if (opaqueBackground && !isSvg) {
    const cut = keyOutBackground(px, w, h);
    const c2 = document.createElement('canvas');
    c2.width = w;
    c2.height = h;
    c2.getContext('2d').putImageData(new ImageData(cut.px, w, h), 0, 0);
    color = c2.toDataURL('image/png');
    colorLum = cut.meanLum;
  }
  // Contrast of the colored logo against a black card (WCAG ratio)
  const colorContrast = (colorLum + 0.05) / 0.05;

  // White ink version:
  //  - dark logos (and logos on a light background): the dark parts become white
  //    ink and light parts drop out, so inner details (white letters on a navy
  //    badge) stay visible as black card showing through
  //  - logos on a dark background: the light parts become white ink
  //  - light logos on transparency: same shape, all white
  const darkIsInk = opaqueBackground ? bgLight : meanLum < 0.35;
  const lightIsInk = opaqueBackground && !bgLight;
  const ramp = (v) => Math.min(1, Math.max(0, (v - 0.15) / 0.55));
  for (let i = 0; i < px.length; i += 4) {
    let a = px[i + 3];
    if (darkIsInk || lightIsInk) {
      const l = lumOf(px[i], px[i + 1], px[i + 2]);
      a = Math.round(a * ramp(darkIsInk ? 1 - l : l));
    }
    px[i] = px[i + 1] = px[i + 2] = 255;
    px[i + 3] = a;
  }
  ctx.putImageData(data, 0, 0);
  const white = canvas.toDataURL('image/png');

  return { original, white, color, colorContrast, aspect, mostlyDark, opaqueBackground, isSvg, meanLum };
}

/**
 * Look at a logo's pixels: does it have its own opaque background (border
 * pixels almost all opaque), is that background light, and how dark is the art.
 */
/**
 * Remove a raster logo's solid background color. Returns a new RGBA buffer and the
 * mean (linear) luminance of what is left. Pixels near the background color fade out;
 * partly transparent edge pixels are un-blended from the background so no halo remains.
 */
function keyOutBackground(src, w, h) {
  let r = 0, g = 0, b = 0, k = 0;
  const add = (i) => { r += src[i]; g += src[i + 1]; b += src[i + 2]; k++; };
  for (let x = 0; x < w; x++) { add(x * 4); add(((h - 1) * w + x) * 4); }
  for (let y = 0; y < h; y++) { add(y * w * 4); add((y * w + w - 1) * 4); }
  const bg = [r / k, g / k, b / k];
  const px = new Uint8ClampedArray(src);
  const lo = 24; // closer than this to the background: fully transparent
  const hi = 110; // further than this: fully opaque
  let sumA = 0, sumL = 0;
  for (let i = 0; i < px.length; i += 4) {
    const d = Math.max(Math.abs(px[i] - bg[0]), Math.abs(px[i + 1] - bg[1]), Math.abs(px[i + 2] - bg[2]));
    const a = Math.min(1, Math.max(0, (d - lo) / (hi - lo)));
    if (a > 0 && a < 1) {
      for (let c = 0; c < 3; c++) px[i + c] = bg[c] + (px[i + c] - bg[c]) / a;
    }
    px[i + 3] = Math.round(px[i + 3] * a);
    if (a > 0.5 && (i & 15) === 0) {
      const l = lumOf(px[i], px[i + 1], px[i + 2]);
      sumA += 1;
      sumL += l * l;
    }
  }
  return { px, meanLum: sumA ? sumL / sumA : 1 };
}

function analyzePixels(px, w, h) {
  let edge = 0, edgeOpaque = 0, edgeLum = 0;
  const sample = (i) => {
    edge++;
    if (px[i + 3] > 240) {
      edgeOpaque++;
      edgeLum += lumOf(px[i], px[i + 1], px[i + 2]);
    }
  };
  for (let x = 0; x < w; x++) { sample(x * 4); sample(((h - 1) * w + x) * 4); }
  for (let y = 0; y < h; y++) { sample(y * w * 4); sample((y * w + w - 1) * 4); }
  const opaqueBackground = edgeOpaque / edge > 0.9;
  const bgLight = opaqueBackground && edgeLum / edgeOpaque > 0.5;
  let sumA = 0, sumL = 0;
  for (let i = 0; i < px.length; i += 16) {
    const a = px[i + 3] / 255;
    if (a < 0.05) continue;
    sumA += a;
    sumL += lumOf(px[i], px[i + 1], px[i + 2]) * a;
  }
  return { opaqueBackground, bgLight, meanLum: sumA ? sumL / sumA : 1 };
}

/**
 * True when a raster logo (File/Blob) has its own opaque background, which
 * would print as a rectangle on a black NFC card. SVGs return false.
 */
export async function logoHasOpaqueBackground(blob) {
  if (!blob || (blob.type || '').includes('svg')) return false;
  const url = URL.createObjectURL(blob);
  try {
    const img = await loadImage(url);
    const scale = Math.min(1, 600 / Math.max(img.naturalWidth, img.naturalHeight));
    const w = Math.max(1, Math.round(img.naturalWidth * scale));
    const h = Math.max(1, Math.round(img.naturalHeight * scale));
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    ctx.drawImage(img, 0, 0, w, h);
    return analyzePixels(ctx.getImageData(0, 0, w, h).data, w, h).opaqueBackground;
  } catch {
    return false;
  } finally {
    URL.revokeObjectURL(url);
  }
}

// Perceived lightness 0-1 of an sRGB pixel (relative luminance, gamma-encoded back)
function lumOf(r, g, b) {
  const lin = (c) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  };
  return Math.sqrt(0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b));
}

/* ---------------------------------------------------------------------- QR */

/**
 * QR modules for a URL as one SVG path (unit = one module, no quiet zone).
 * Error correction Q (25%) for printed cards.
 */
export function qrPath(text) {
  const qr = QRCode.create(text, { errorCorrectionLevel: 'Q' });
  const { size, data } = qr.modules;
  let d = '';
  for (let y = 0; y < size; y++) {
    let x = 0;
    while (x < size) {
      if (!data[y * size + x]) { x++; continue; }
      let run = 1;
      while (x + run < size && data[y * size + x + run]) run++;
      d += `M${x} ${y}h${run}v1h-${run}z`;
      x += run;
    }
  }
  return { d, size };
}

/* ---------------------------------------------------------------- exports */

/**
 * Fetch the profile photo (from Storage) for the photo layouts.
 * Returns { original, white, aspect }:
 *  - original: JPEG data URL, at most maxPx on the long side
 *  - white: PNG data URL for white ink only: brightness becomes white-ink coverage,
 *    so the photo prints as a one-color image on the black card
 */
export async function preparePhotoArt(url, maxPx = 1200) {
  const res = await fetch(url, { mode: 'cors' });
  if (!res.ok) throw new Error('Photo could not be loaded');
  const src = await blobToDataUrl(await res.blob());
  const img = await loadImage(src);
  const nw = img.naturalWidth || maxPx;
  const nh = img.naturalHeight || maxPx;
  const scale = Math.min(1, maxPx / Math.max(nw, nh));
  const w = Math.max(1, Math.round(nw * scale));
  const h = Math.max(1, Math.round(nh * scale));
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  ctx.fillStyle = '#FFFFFF'; // photos with transparency get a white backdrop
  ctx.fillRect(0, 0, w, h);
  ctx.drawImage(img, 0, 0, w, h);
  const original = canvas.toDataURL('image/jpeg', 0.92);
  const data = ctx.getImageData(0, 0, w, h);
  const px = data.data;
  for (let i = 0; i < px.length; i += 4) {
    const l = lumOf(px[i], px[i + 1], px[i + 2]);
    px[i] = px[i + 1] = px[i + 2] = 255;
    px[i + 3] = Math.round(255 * Math.min(1, Math.max(0, (l - 0.04) / 0.92)));
  }
  ctx.putImageData(data, 0, 0);
  return { original, white: canvas.toDataURL('image/png'), aspect: w / h };
}

/**
 * Draw an SVG (string) onto a transparent canvas at the given DPI and return a PNG blob.
 * whiteLayer: return the white underbase mask instead: every inked pixel becomes
 * solid white, alpha follows the ink coverage, transparent stays transparent.
 */
export async function svgToPng(svgString, dpi = DEFAULT_DPI, { whiteLayer = false } = {}) {
  const { width, height } = cardPixels(dpi);
  // Rasterize at the final pixel size so vectors and text stay sharp
  const sized = svgString.replace(/<svg([^>]*?)\swidth="[^"]*"\sheight="[^"]*"/, `<svg$1 width="${width}" height="${height}"`);
  const url = URL.createObjectURL(new Blob([sized], { type: 'image/svg+xml' }));
  try {
    const img = await loadImage(url);
    if (img.decode) await img.decode().catch(() => {});
    // Give embedded fonts/images a frame to settle before drawing
    await new Promise((r) => requestAnimationFrame(() => setTimeout(r, 30)));
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, width, height); // transparent: never paint a background
    ctx.drawImage(img, 0, 0, width, height);
    if (whiteLayer) {
      const data = ctx.getImageData(0, 0, width, height);
      const px = data.data;
      for (let i = 0; i < px.length; i += 4) {
        if (px[i + 3]) { px[i] = 255; px[i + 1] = 255; px[i + 2] = 255; }
        else { px[i] = 0; px[i + 1] = 0; px[i + 2] = 0; }
      }
      ctx.putImageData(data, 0, 0);
    }
    const blob = await new Promise((resolve, reject) =>
      canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('PNG export failed'))), 'image/png'));
    return withDpi(blob, dpi);
  } finally {
    URL.revokeObjectURL(url);
  }
}

/** Add a pHYs chunk so print software reads the intended DPI. */
async function withDpi(pngBlob, dpi) {
  const bytes = new Uint8Array(await pngBlob.arrayBuffer());
  const ppm = Math.round(dpi / 0.0254);
  const chunk = new Uint8Array(21);
  const view = new DataView(chunk.buffer);
  view.setUint32(0, 9);
  chunk.set([0x70, 0x48, 0x59, 0x73], 4); // "pHYs"
  view.setUint32(8, ppm);
  view.setUint32(12, ppm);
  chunk[16] = 1; // unit: metre
  view.setUint32(17, crc32(chunk.subarray(4, 17)));
  // Insert right after IHDR (8-byte signature + 25-byte IHDR chunk)
  const at = 33;
  const out = new Uint8Array(bytes.length + chunk.length);
  out.set(bytes.subarray(0, at), 0);
  out.set(chunk, at);
  out.set(bytes.subarray(at), at + chunk.length);
  return new Blob([out], { type: 'image/png' });
}

let crcTable = null;
function crc32(buf) {
  if (!crcTable) {
    crcTable = new Uint32Array(256);
    for (let n = 0; n < 256; n++) {
      let c = n;
      for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
      crcTable[n] = c >>> 0;
    }
  }
  let c = 0xffffffff;
  for (let i = 0; i < buf.length; i++) c = crcTable[(c ^ buf[i]) & 0xff] ^ (c >>> 8);
  return (c ^ 0xffffffff) >>> 0;
}

export function downloadBlob(blob, name) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export const svgBlob = (svgString) => new Blob([svgString], { type: 'image/svg+xml' });
